import { useEffect, useRef, useState } from 'react';
import QuestionsList from '../../../component/EliteGame/QuestionsList';
import CreateQuestions from '../../../component/EliteGame/CreateQuestions';
import {
  createQuestion,
  GetMainCategories,
  GetSubCategories,
  getQuestionsByQuizType,
  deleteQuestion,
  updateQuestion
} from '../../../api/eliteGameApi';
import { ELITE_QUIZ_TYPE } from '../../../utils/constants';
import ViewQuestionModal from '../../../component/EliteGame/ViewQuestionModal';
import { useEliteGames } from '../../../context/EliteGamesContext';
import { errorToast, successToast } from '../../../utils/helper';
import DeletePopup from '../../../component/common/modals/DeletePopup';

const DailyQuizManageQuestions = () => {
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [questionId, setQuestionId] = useState(null);
  const [open, setOpen] = useState(false);
  const formRef = useRef(null);

  // Fetch main categories on component mount
  const handleView = (question) => {
    setViewQuestion(question);
    setViewModalOpen(true);
  };
  const { setLoading } = useEliteGames();

  const getQuestions = async (filter = {}) => {
    setLoading(true);
    try {
      const data = await getQuestionsByQuizType(ELITE_QUIZ_TYPE.DAILY_QUIZ, {
        page,
        limit,
        ...filter
      });
      setQuestions(data || []);
    } catch (error) {
      errorToast('Failed to fetch questions', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch questions when component mounts or refresh changes
  useEffect(() => {
    getQuestions();
  }, [refresh, page, limit]);

  // Fetch categories for filters (optional but aligns with Quiz Zone behavior)
  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const cats = await GetMainCategories({ quizType: ELITE_QUIZ_TYPE.DAILY_QUIZ }, true);
        setMainCategories(cats.data || []);
      } catch (err) {
        // non-fatal
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const getSubCategories = async (mainCategoryId) => {
    setLoading(true);
    try {
      const data = await GetSubCategories(
        { categoryId: mainCategoryId, quizType: ELITE_QUIZ_TYPE.DAILY_QUIZ },
        true
      );
      setSubCategories(data.data || []);
    } catch (error) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  // Handle edit action for a question
  const handleEdit = (question) => {
    setSelectedQuestion(question);
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Handle filter action for questions
  const handleFilter = (filter) => {
    getQuestions(filter);
  };
  /**
   * Handle form submission for creating or updating a question
   * @param {*} data
   */
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const formData = {
        ...data,
        quizType: ELITE_QUIZ_TYPE.DAILY_QUIZ,
        options: [
          data.optionA,
          data.optionB,
          ...(data.questionType != 2 ? [data.optionC, data.optionD] : [])
        ],
        correctAnswer: data[data.answer.value || data.answer]
      };

      // If a question is selected, update it; otherwise, create a new question
      if (selectedQuestion) {
        const response = await updateQuestion(formData, selectedQuestion._id);
        successToast('Question updated successfully', response.message);
        setSelectedQuestion(null); // Clear selected question after update
      } else {
        // Create a new question
        const response = await createQuestion(formData);
        setRefreshFlag((prev) => prev + 1);
        successToast('Question created successfully', response.message);
      }
      getQuestions(); // Refresh questions list after creating a new question
      setSelectedQuestion({});
      setSelectedQuestion(null); // Clear selected question after submission
    } catch (error) {
      errorToast('Failed to create question. Please try again.');
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh);
  };

  // Handle refresh action for questions
  const handleRefresh = () => {
    // Reset page to 1 and refresh questions
    setPage(1);
    getQuestions(); // Fetch fresh data
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      // Call API to delete question
      await deleteQuestion(questionId);
      successToast('Question deleted successfully');
      setRefresh(true); // Refresh questions list after deletion
    } catch (error) {
      errorToast('Error deleting question:', error);
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh);
  };
  return (
    <div>
      <div ref={formRef}>
        <CreateQuestions
          onSubmit={onSubmit}
          name={'Daily Quiz'}
          quizType={ELITE_QUIZ_TYPE.DAILY_QUIZ}
          existingQuestion={selectedQuestion}
          resetTrigger={refreshFlag}
        />
      </div>
      <div className="bg-white rounded-lg shadow p-6 m-4">
        <div className="mb-2">
          <span className="text-2xl font-bold text-black-600">Questions for Quiz Zone</span>
        </div>
        <hr className="mb-6" />
        <QuestionsList
          quizType={ELITE_QUIZ_TYPE.DAILY_QUIZ}
          questions={questions}
          categoryList={mainCategories}
          subCategoryList={subCategories}
          getSubCategories={getSubCategories}
          onEdit={handleEdit}
          page={page}
          limit={limit}
          setLimit={setLimit}
          onFilter={handleFilter}
          onRefresh={handleRefresh}
          setPage={setPage}
          onView={handleView}
          onDelete={(id) => {
            setQuestionId(id);
            setOpen(true);
          }}
        />
        {DeletePopup({
          title: 'Confirm Deletion',
          open,
          setOpen,
          message:
            'Are you sure you want to delete this question? All related data will also be deleted.',
          setDelete: handleDelete
        })}
      </div>

      <ViewQuestionModal
        questions={viewQuestion}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
    </div>
  );
};

export default DailyQuizManageQuestions;
